import { computeSortPrice } from '../../common/SearchFilterBar/SearchFilterBar';

/**
 * Price math for the binder dashboard. Cards embed their prices as
 * JSON arrays index-aligned with card.finishes (min_prices[i] is the
 * lowest of TCGplayer/CardKingdom for finishes[i]), but mysql2 may
 * hand them over as arrays or strings - always parse defensively.
 */

export function parseArray(val) {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try { return JSON.parse(val); } catch { return []; }
    }
    return [];
}

// Lowest listed price for one binder entry, finish-accurate: a foil
// entry prices as the foil printing. 'any' wishes (and finishes the
// card doesn't know) fall back to the cheapest finish.
export function entryPrice(card, finish) {
    if (!card) return null;
    const finishes = parseArray(card.finishes);
    const idx = finish ? finishes.indexOf(finish) : -1;
    if (idx >= 0) {
        const candidates = [
            parseArray(card.min_prices)[idx],
            parseArray(card.tcgplayer_prices)[idx],
            parseArray(card.cardkingdom_prices)[idx],
        ].map(p => parseFloat(p)).filter(p => !isNaN(p) && p > 0);
        if (candidates.length) return Math.min(...candidates);
    }
    return computeSortPrice(card);
}

// Per-finish price table in the shape CardInfoOverlay expects
// (same derivation SearchFilterBar runs for its own overlay).
export function buildPricesByFinish(card) {
    if (!card) return {};
    const finishes = parseArray(card.finishes);
    if (finishes.length === 0) return {};

    const tcgplayerPrices = parseArray(card.tcgplayer_prices);
    const cardkingdomPrices = parseArray(card.cardkingdom_prices);
    const minPrices = parseArray(card.min_prices);

    const pricesByFinish = {};
    finishes.forEach((finish, index) => {
        const tcgPrice = tcgplayerPrices[index] !== undefined ? parseFloat(tcgplayerPrices[index]) : null;
        const ckPrice = cardkingdomPrices[index] !== undefined ? parseFloat(cardkingdomPrices[index]) : null;
        const minPrice = minPrices[index] !== undefined ? parseFloat(minPrices[index]) : null;

        const validTcg = tcgPrice !== null && !isNaN(tcgPrice) && tcgPrice > 0 ? tcgPrice : null;
        const validCk = ckPrice !== null && !isNaN(ckPrice) && ckPrice > 0 ? ckPrice : null;
        const validMin = minPrice !== null && !isNaN(minPrice) && minPrice > 0 ? minPrice : null;

        let usdPrice = validMin;
        let bestSource = null;
        if (!usdPrice) {
            const sources = [
                { price: validTcg, source: 'tcgplayer' },
                { price: validCk, source: 'cardkingdom' },
            ];
            for (const { price, source } of sources) {
                if (price !== null && (usdPrice === null || price < usdPrice)) {
                    usdPrice = price;
                    bestSource = source;
                }
            }
        } else {
            if (validTcg === validMin) bestSource = 'tcgplayer';
            else if (validCk === validMin) bestSource = 'cardkingdom';
        }

        pricesByFinish[finish] = {
            setCode: card.setCode,
            tcgplayer_price: validTcg !== null ? validTcg.toFixed(2) : null,
            cardkingdom_price: validCk !== null ? validCk.toFixed(2) : null,
            usd_price: usdPrice !== null ? usdPrice.toFixed(2) : null,
            best_source: bestSource,
        };
    });
    return pricesByFinish;
}

export function formatMoney(value) {
    return '$' + (value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
